import "bulma/css/bulma.min.css";
import "@fortawesome/fontawesome-free/js/all";
import React, { useState, useContext, useEffect } from "react";
import _ from "lodash";
import { useParams, useLocation } from "react-router-dom";
import { AppContext, DispatchContext } from "../../state/Context";
import { trackAppointment, updateTrackAppointment } from "../../state/actions/workflow";
import { HospitalLogo } from "../Shared/HospitalLogo";

const TrackAppointment = () => {
  const { id } = useParams();
  const location = useLocation();
  const [secret, setSecretCode] = useState("");
  const [nextSession, setNextSession] = useState("");
  const [itemNumber, setItemNumber] = useState("");
  const [isLetterReady, setIsLetterReady] = useState("");
  const [testRequired, setTestRequired] = useState("");
  const [notes, setNotes] = useState("");

  const { appointment, verifyError, isVerifying, isSubmitting, submittingError, submittingMessage } = useContext(
    AppContext
  );
  const dispatch = useContext(DispatchContext);

  useEffect(() => {
    setNextSession(_.get(appointment, "trackedAppointment.nextSession", ""));
    setItemNumber(_.get(appointment, "trackedAppointment.itemNumber", ""));
    setIsLetterReady(_.get(appointment, "trackedAppointment.isLetterReady", ""));
    setTestRequired(_.get(appointment, "trackedAppointment.testRequired", ""));
    setNotes(_.get(appointment, "trackedAppointment.notes", ""));
  }, [appointment]);

  useEffect(() => {
    if (!_.isEmpty(location.search)) {
      setSecretCode(location.search.split("secretCode=")[1]);
    }
  }, [location.search]);

  const handleVerifySecretCode = (e) => {
    e.preventDefault();

    if (_.isEmpty(secret) || _.isEmpty(id)) {
      dispatch({ type: "SET_VERIFY_ERROR", payload: { verifyError: true } });
      return;
    }

    trackAppointment({ appointmentId: id, secretCode: secret }, null, dispatch);
  };

  const handleSecretCodeChange = (e) => {
    e.preventDefault();
    verifyError && dispatch({ type: "SET_VERIFY_ERROR", payload: { verifyError: false } });
    setSecretCode(e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateTrackAppointment(
      {
        id,
        secret,
        nextSession,
        itemNumber,
        isLetterReady,
        testRequired,
        notes,
      },
      { submittingError, submittingMessage },
      dispatch
    );
  };

  if (_.isEmpty(appointment)) {
    return (
      <div className="container is-fluid">
        <div className="columns is-mobile is-size-7 is-centered">
          <div className="column is-three-quarters-mobile is-two-thirds-tablet is-half-desktop is-one-third-widescreen is-one-quarter-fullhd">
            <hr />
            <div className="field">
              <HospitalLogo />
              <div className="control">
                <input
                  className={`${verifyError ? "is-danger " : ""}input is-size-7 is-fullwidth is-radiusless`}
                  type="text"
                  placeholder="secret code"
                  value={secret}
                  onChange={handleSecretCodeChange}
                  disabled={isVerifying}
                />
              </div>
            </div>
            <div className="field">
              <div className="control">
                <button
                  className="button is-link is-size-7 is-fullwidth is-radiusless"
                  disabled={isVerifying}
                  onClick={handleVerifySecretCode}
                >
                  {isVerifying ? "Verifying ..." : "Verify"}
                </button>
              </div>
            </div>
            {isVerifying && <progress className="progress is-small is-link is-radiusless" max="100"></progress>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container is-fluid">
      <div className="columns is-mobile is-centered">
        <div className="column is-full-mobile is-two-thirds-tablet is-half-desktop is-half-widescreen is-half-fullhd">
          <HospitalLogo />
          <hr />
          <div className="columns is-mobile is-size-7">
            <div className="column is-half-mobile is-one-third-tablet has-text-right">Patient name:</div>
            <div className="column is-half-mobile is-two-thirds-tablet">{appointment.patientName}</div>
          </div>
          <div className="columns is-mobile is-size-7">
            <div className="column is-half-mobile is-one-third-tablet has-text-right">Appointment date:</div>
            <div className="column is-half-mobile is-two-thirds-tablet">{appointment.dateOfService}</div>
          </div>
          <div className="columns is-mobile is-size-7">
            <div className="column is-half-mobile is-one-third-tablet has-text-right">Referring GP:</div>
            <div className="column is-half-mobile is-two-thirds-tablet">{appointment.referrerNames}</div>
          </div>
          <hr />
          <div className="columns is-mobile is-size-7 is-vcentered">
            <div className="column is-half-mobile is-one-third-tablet has-text-right">Next session:</div>
            <div className="column is-half-mobile is-two-thirds-tablet">
              <div className="field">
                <div className="control is-size-7">
                  <div className="select is-fullwidth">
                    <select
                      disabled={isSubmitting}
                      className="is-size-7 is-radiusless"
                      onChange={(e) => setNextSession(e.target.value)}
                      value={nextSession}
                    >
                      <option value="">Please select next session</option>
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((week) => (
                        <option key={week} value={week}>
                          {week === 0 ? "no next session" : `${week} weeks later`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="columns is-mobile is-size-7 is-vcentered">
            <div className="column is-half-mobile is-one-third-tablet has-text-right">Consult item number:</div>
            <div className="column is-half-mobile is-two-thirds-tablet">
              <div className="field">
                <div className="control is-size-7">
                  <input
                    disabled={isSubmitting}
                    className="input is-radiusless is-size-7"
                    type="text"
                    onChange={(e) => setItemNumber(e.target.value)}
                    value={itemNumber}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="columns is-mobile is-size-7 is-vcentered">
            <div className="column is-half-mobile is-one-third-tablet has-text-right">Is the letter ready:</div>
            <div className="column is-half-mobile is-two-thirds-tablet">
              <div className="field">
                <div className="control is-size-7">
                  <label className="radio radio-label">
                    <input
                      disabled={isSubmitting}
                      type="radio"
                      name="letter"
                      value="yes"
                      checked={isLetterReady === "yes"}
                      onChange={(e) => setIsLetterReady("yes")}
                    />{" "}
                    Yes
                  </label>
                  <label className="radio radio-label">
                    <input
                      disabled={isSubmitting}
                      type="radio"
                      name="letter"
                      value="no"
                      checked={isLetterReady === "no"}
                      onChange={(e) => setIsLetterReady("no")}
                    />{" "}
                    No
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="columns is-mobile is-size-7 is-vcentered">
            <div className="column is-half-mobile is-one-third-tablet has-text-right">Follow-up tests required:</div>
            <div className="column is-half-mobile is-two-thirds-tablet">
              <div className="field">
                <div className="control is-size-7">
                  <label className="radio radio-label">
                    <input
                      disabled={isSubmitting}
                      type="radio"
                      name="test"
                      value="yes"
                      checked={testRequired === "yes"}
                      onChange={(e) => setTestRequired("yes")}
                    />{" "}
                    Yes
                  </label>
                  <label className="radio radio-label">
                    <input
                      disabled={isSubmitting}
                      type="radio"
                      name="test"
                      value="no"
                      checked={testRequired === "no"}
                      onChange={(e) => setTestRequired("no")}
                    />{" "}
                    No
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="columns is-mobile is-size-7">
            <div className="column is-half-mobile is-one-third-tablet has-text-right">Appointment notes:</div>
            <div className="column is-half-mobile is-two-thirds-tablet">
              <div className="field">
                <div className="control is-size-7">
                  <textarea
                    disabled={isSubmitting}
                    className="textarea is-radiusless is-size-7"
                    onChange={(e) => setNotes(e.target.value)}
                    value={notes}
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
          <hr />
          <div className="columns is-mobile is-size-7 is-vcentered">
            <div
              className={`column is-two-thirds has-text-right ${
                submittingError ? "has-text-danger" : "has-text-success"
              }`}
            >
              {submittingMessage}
            </div>
            <div className="column is-one-third">
              <button
                disabled={isSubmitting}
                className="button is-fullwidth is-radiusless is-link is-size-7"
                onClick={handleSubmit}
              >
                {isSubmitting ? "Submitting ..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackAppointment;
